import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormat';

// System Admin > Affiliates: the program's switch and default terms, the affiliates (accountants and others who refer customers), and which
// organization each referred. What they earn is in Partner Commissions.

const errorText = (e, fallback) => (typeof e.response?.data === 'string' && e.response.data ? e.response.data : fallback);
const blank = { name: '', contactPerson: '', email: '', phone: '', notes: '', isActive: true, initialRatePercent: '', trailingRatePercent: '', initialPeriodMonths: '', capMonths: '' };
const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

function Affiliates() {
  const [program, setProgram] = useState(null);
  const [programForm, setProgramForm] = useState(null);
  const [affiliates, setAffiliates] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [edit, setEdit] = useState(null); // null, or the affiliate form (with id when editing)
  const [attach, setAttach] = useState({ organizationId: '', partnerId: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, a, r] = await Promise.all([
        axios.get('/api/system-admin/affiliate-program'),
        axios.get('/api/system-admin/affiliates'),
        axios.get('/api/system-admin/affiliate-referrals'),
      ]);
      setProgram(p.data);
      setProgramForm({
        enabled: p.data.enabled, initialRatePercent: p.data.initialRatePercent, trailingRatePercent: p.data.trailingRatePercent,
        initialPeriodMonths: p.data.initialPeriodMonths, capMonths: p.data.capMonths ?? '',
      });
      setAffiliates(a.data);
      setReferrals(r.data);
    } catch (e) {
      toast.error(errorText(e, 'Could not load the affiliate program'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get('/api/system-admin/organizations').then((r) => setOrganizations((r.data || []).filter((o) => !o.internal))).catch(() => {});
  }, []);

  const saveProgram = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.put('/api/system-admin/affiliate-program', {
        enabled: programForm.enabled,
        initialRatePercent: num(programForm.initialRatePercent),
        trailingRatePercent: num(programForm.trailingRatePercent),
        initialPeriodMonths: num(programForm.initialPeriodMonths),
        capMonths: num(programForm.capMonths),
      });
      toast.success('Program saved');
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not save'));
    }
    setBusy(false);
  };

  const saveAffiliate = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      name: edit.name, contactPerson: edit.contactPerson, email: edit.email, phone: edit.phone, notes: edit.notes, isActive: edit.isActive,
      initialRatePercent: num(edit.initialRatePercent), trailingRatePercent: num(edit.trailingRatePercent),
      initialPeriodMonths: num(edit.initialPeriodMonths), capMonths: num(edit.capMonths),
    };
    try {
      if (edit.id) await axios.put(`/api/system-admin/affiliates/${edit.id}`, body);
      else await axios.post('/api/system-admin/affiliates', body);
      toast.success('Saved');
      setEdit(null);
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not save'));
    }
    setBusy(false);
  };

  const invite = async (a) => {
    try {
      const { data } = await axios.post(`/api/system-admin/affiliates/${a.id}/invite`);
      toast.success(data?.message || 'Done');
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not invite'));
    }
  };

  const attachAffiliate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/system-admin/organizations/${attach.organizationId}/affiliate-referral`, { partnerId: Number(attach.partnerId) });
      toast.success('Affiliate attached');
      setAttach({ organizationId: '', partnerId: '' });
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not attach'));
    }
  };

  const endReferral = async (r) => {
    if (!window.confirm(`Stop ${r.affiliate} earning on ${r.organization} from now on? What was earned stays.`)) return;
    try {
      await axios.delete(`/api/system-admin/affiliate-referrals/${r.id}`);
      toast.success('Referral ended');
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not end it'));
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); } catch (e) { toast.error('Could not copy'); }
  };

  if (!program || !affiliates) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>;

  const field = (label, name, extra = {}) => (
    <Form.Group className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control value={edit[name] ?? ''} onChange={(e) => setEdit({ ...edit, [name]: e.target.value })} {...extra} />
    </Form.Group>
  );
  const activeAffiliates = affiliates.filter((a) => a.isActive);

  return (
    <div data-testid="affiliates-admin">
      <Card className="mb-4">
        <Card.Header><strong>Program</strong></Card.Header>
        <Card.Body>
          <Form onSubmit={saveProgram}>
            <Form.Check
              type="switch" id="affiliate-program-enabled" className="mb-3"
              label="The affiliate program is on (sign-up shows a Referred by box and affiliate codes work)"
              checked={programForm.enabled}
              onChange={(e) => setProgramForm({ ...programForm, enabled: e.target.checked })}
            />
            <Row>
              <Col md={3}><Form.Group className="mb-3"><Form.Label>First-period rate (%)</Form.Label>
                <Form.Control type="number" step="0.01" min="0" max="100" value={programForm.initialRatePercent} onChange={(e) => setProgramForm({ ...programForm, initialRatePercent: e.target.value })} data-testid="program-initial-rate" /></Form.Group></Col>
              <Col md={3}><Form.Group className="mb-3"><Form.Label>Months the first period lasts</Form.Label>
                <Form.Control type="number" min="0" value={programForm.initialPeriodMonths} onChange={(e) => setProgramForm({ ...programForm, initialPeriodMonths: e.target.value })} /></Form.Group></Col>
              <Col md={3}><Form.Group className="mb-3"><Form.Label>Rate after that (%)</Form.Label>
                <Form.Control type="number" step="0.01" min="0" max="100" value={programForm.trailingRatePercent} onChange={(e) => setProgramForm({ ...programForm, trailingRatePercent: e.target.value })} /></Form.Group></Col>
              <Col md={3}><Form.Group className="mb-3"><Form.Label>Earns for (months)</Form.Label>
                <Form.Control type="number" min="1" value={programForm.capMonths} placeholder="For ever" onChange={(e) => setProgramForm({ ...programForm, capMonths: e.target.value })} /></Form.Group></Col>
            </Row>
            <Form.Text className="text-muted d-block mb-3">
              These are the terms a new referral gets (unless the affiliate has its own below). A referral keeps the terms it was made with, so
              changing them here never changes what an affiliate was promised. An account manager on the same customer is paid separately and
              is not reduced by the affiliate. {program.updatedBy && `Last changed by ${program.updatedBy}.`}
            </Form.Text>
            <Button type="submit" disabled={busy}>Save program</Button>
          </Form>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Affiliates</strong>
          <Button size="sm" onClick={() => setEdit({ ...blank })} data-testid="add-affiliate">Add affiliate</Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive className="mb-0">
            <thead><tr><th>Name</th><th>Code</th><th>Terms</th><th>Referred</th><th>Login</th><th>Added by</th><th /></tr></thead>
            <tbody>
              {affiliates.length === 0 ? (
                <tr><td colSpan="7" className="text-center text-muted py-3">No affiliates yet.</td></tr>
              ) : affiliates.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}{!a.isActive && <Badge bg="secondary" className="ms-2">Inactive</Badge>}<div className="small text-muted">{a.email}</div></td>
                  <td><code>{a.referralCode}</code> <Button variant="link" size="sm" className="p-0 ms-1" onClick={() => copy(a.link)}>Copy link</Button></td>
                  <td className="small">
                    {a.effectiveTerms.initialRatePercent}% for {a.effectiveTerms.initialPeriodMonths} mo, then {a.effectiveTerms.trailingRatePercent}%
                    {a.effectiveTerms.capMonths ? `, to month ${a.effectiveTerms.capMonths}` : ''}
                    {(a.initialRatePercent != null || a.trailingRatePercent != null || a.initialPeriodMonths != null || a.capMonths != null) && <Badge bg="info" className="ms-1">Own terms</Badge>}
                  </td>
                  <td>{a.activeReferrals}</td>
                  <td>{a.hasLogin ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                  <td className="small">{a.addedBy || <span className="text-muted">You</span>}</td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="outline-primary" className="me-2" onClick={() => invite(a)}>{a.hasLogin ? 'Resend invite' : 'Invite'}</Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setEdit({ ...blank, ...Object.fromEntries(Object.entries(a).map(([k, v]) => [k, v ?? ''])) })}>Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header><strong>Referred organizations</strong></Card.Header>
        <Card.Body>
          <Form onSubmit={attachAffiliate} className="row g-2 align-items-end mb-3">
            <Col md={5}>
              <Form.Label className="small mb-1">Customer forgot the code? Attach an affiliate to an organization</Form.Label>
              <Form.Select value={attach.organizationId} onChange={(e) => setAttach({ ...attach, organizationId: e.target.value })} required>
                <option value="">Organization...</option>
                {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Select value={attach.partnerId} onChange={(e) => setAttach({ ...attach, partnerId: e.target.value })} required>
                <option value="">Affiliate...</option>
                {activeAffiliates.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}><Button type="submit" variant="outline-primary" className="w-100">Attach</Button></Col>
          </Form>
          <Table responsive className="mb-0">
            <thead><tr><th>Organization</th><th>Affiliate</th><th>Since</th><th>How</th><th>Terms</th><th>Status</th><th /></tr></thead>
            <tbody>
              {referrals.length === 0 ? (
                <tr><td colSpan="7" className="text-center text-muted py-3">No referrals yet.</td></tr>
              ) : referrals.map((r) => (
                <tr key={r.id}>
                  <td>{r.organization}</td>
                  <td>{r.affiliate}</td>
                  <td>{formatDate(r.startDate)}</td>
                  <td>{r.source === 'SIGNUP' ? 'Named at sign-up' : 'Attached by hand'}</td>
                  <td className="small">{r.initialRatePercent}% for {r.initialPeriodMonths} mo, then {r.trailingRatePercent}%{r.capMonths ? `, to month ${r.capMonths}` : ''}</td>
                  <td>{r.status === 'ACTIVE' ? <Badge bg="success">Earning</Badge> : <Badge bg="secondary">Ended {r.endDate ? formatDate(r.endDate) : ''}</Badge>}</td>
                  <td className="text-end">{r.status === 'ACTIVE' && <Button size="sm" variant="outline-danger" onClick={() => endReferral(r)}>End</Button>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={!!edit} onHide={() => setEdit(null)} centered>
        {edit && (
          <Form onSubmit={saveAffiliate}>
            <Modal.Header closeButton><Modal.Title>{edit.id ? 'Edit affiliate' : 'Add affiliate'}</Modal.Title></Modal.Header>
            <Modal.Body>
              {field('Name (the firm or person)', 'name', { required: true, 'data-testid': 'affiliate-name' })}
              {field('Contact person', 'contactPerson')}
              {field('Email (their login, and where we write to them)', 'email', { type: 'email', required: true, disabled: !!edit.id, 'data-testid': 'affiliate-email' })}
              {field('Phone', 'phone')}
              {field('Notes', 'notes', { as: 'textarea', rows: 2 })}
              <hr />
              <p className="small text-muted mb-2">Own terms for this affiliate. Leave all empty to use the program&apos;s. They apply to new referrals only.</p>
              <Row>
                <Col xs={6}>{field('First-period rate (%)', 'initialRatePercent', { type: 'number', step: '0.01', min: 0, max: 100 })}</Col>
                <Col xs={6}>{field('First period (months)', 'initialPeriodMonths', { type: 'number', min: 0 })}</Col>
                <Col xs={6}>{field('Rate after (%)', 'trailingRatePercent', { type: 'number', step: '0.01', min: 0, max: 100 })}</Col>
                <Col xs={6}>{field('Earns for (months)', 'capMonths', { type: 'number', min: 1, placeholder: 'For ever' })}</Col>
              </Row>
              {edit.id && (
                <Form.Check type="switch" id="affiliate-active" label="Active (an inactive affiliate's code stops working and it earns nothing new)"
                  checked={!!edit.isActive} onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} />
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setEdit(null)}>Cancel</Button>
              <Button type="submit" disabled={busy}>Save</Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>
      {affiliates.length === 0 && <Alert variant="info" className="mt-3">Add an affiliate, then <strong>Invite</strong> them so they can sign in and see their link and earnings.</Alert>}
    </div>
  );
}

export default Affiliates;
